/**
 * platform-feedback-page.tsx — Platform → Góp ý của khách (super-admin). Route `/platform/feedback`.
 *
 * Khách bấm nút "Gửi Feedback" trong app → góp ý nằm ở đây: lọc theo trạng thái / loại / tìm kiếm, mở chi tiết
 * (nội dung, ảnh, trang, trình duyệt, lỗi JS + API đính kèm), đổi trạng thái và ghi chú nội bộ.
 * Góp ý MỚI hiện chấm đỏ ở menu; mở chi tiết một góp ý "Mới" thì tự chuyển sang "Đang xem" (hết chấm đỏ).
 * Backend: `modules/platform/platform-feedback-routes.ts`.
 */
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { BellRing, Download, ImageOff, Loader2, Search } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { platformApiError } from '@/lib/platform-client'
import { cn } from '@/lib/utils'
import { downloadFeedbackExport, useFeedbackImage, useFeedbackList, useLarkSettings, useUpdateFeedback } from '@/hooks/use-platform-feedback'
import { LarkSettingsDialog, LarkStatusDot } from './platform-feedback-lark-dialog'
import {
  STATUS_LABEL, STATUS_ORDER, STATUS_TONE, TYPE_LABEL, snippet,
  type FeedbackItem, type FeedbackKind, type FeedbackStatus,
} from './feedback-helpers'

const PAGE_SIZE = 20
const fmt = (iso: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY HH:mm') : '—')

/** Giờ của một dòng chẩn đoán; thiếu / sai định dạng thì để trống thay vì "Invalid Date". */
const timeOf = (at: string) => { const d = dayjs(at); return at && d.isValid() ? d.format('HH:mm:ss') : '' }

function StatusBadge({ status }: { status: FeedbackStatus }) {
  return <span className={cn('inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium', STATUS_TONE[status])}>{STATUS_LABEL[status]}</span>
}

export function PlatformFeedbackPage() {
  const [status, setStatus] = useState<FeedbackStatus | ''>('')
  const [type, setType] = useState('')
  const [q, setQ] = useState('')
  const [exporting, setExporting] = useState(false)
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<FeedbackItem | null>(null)
  const [larkOpen, setLarkOpen] = useState(false)
  const { data: lark } = useLarkSettings()
  const update = useUpdateFeedback()

  // Gõ xong mới tìm (tránh gọi API mỗi phím).
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); setPage(1) }, 350)
    return () => clearTimeout(t)
  }, [q])

  const { data, isLoading, isError } = useFeedbackList({ status, type, q: debouncedQ, page, pageSize: PAGE_SIZE })
  const counts = data?.counts

  const open = (f: FeedbackItem) => {
    setSelected(f)
    // Mở một góp ý còn "Mới" → coi như đã nhận, chuyển "Đang xem" để chấm đỏ giảm đi.
    if (f.status === 'new') {
      update.mutate({ id: f.id, status: 'reviewing' }, { onSuccess: (u) => setSelected((cur) => (cur?.id === u.id ? u : cur)) })
    }
  }

  const columns: Column<FeedbackItem>[] = [
    { key: 'type', header: 'Loại', cell: (f) => <span className="whitespace-nowrap text-sm">{TYPE_LABEL[f.type] ?? f.type}</span> },
    {
      key: 'message', header: 'Nội dung',
      cell: (f) => (
        <div className="min-w-[220px] max-w-[420px]">
          <p className={cn('line-clamp-2 text-sm', f.status === 'new' && 'font-medium')}>{snippet(f.message, 160)}</p>
          {f.images.length > 0 && <p className="text-xs text-muted-foreground">📎 {f.images.length} ảnh</p>}
        </div>
      ),
    },
    {
      key: 'org', header: 'Tổ chức · người gửi',
      cell: (f) => (
        <div className="min-w-0 max-w-[220px]">
          <p className="truncate text-sm">{f.orgName || '—'}</p>
          <p className="truncate text-xs text-muted-foreground">{f.userName || f.userEmail}</p>
        </div>
      ),
    },
    { key: 'at', header: 'Gửi lúc', cell: (f) => <span className="whitespace-nowrap text-xs text-muted-foreground">{fmt(f.createdAt)}</span> },
    { key: 'status', header: 'Trạng thái', cell: (f) => <StatusBadge status={f.status} /> },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Góp ý của khách"
        description="Góp ý, báo lỗi và order tính năng từ nút “Gửi Feedback” trong ứng dụng."
        actions={
          <Button variant="outline" onClick={() => setLarkOpen(true)}>
            <BellRing />Thông báo Lark<LarkStatusDot active={lark?.active} />
          </Button>
        }
      />
      <LarkSettingsDialog open={larkOpen} onClose={() => setLarkOpen(false)} />

      {/* Tab trạng thái + số đếm; "Mới" có chấm đỏ khi còn góp ý chưa xem. */}
      <div className="flex flex-wrap gap-1.5">
        {([['', 'Tất cả'], ...STATUS_ORDER.map((s) => [s, STATUS_LABEL[s]] as const)] as Array<[FeedbackStatus | '', string]>).map(([value, label]) => {
          const n = counts?.[value === '' ? 'all' : value]
          const active = status === value
          return (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => { setStatus(value); setPage(1) }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors',
                active ? 'border-primary bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:border-primary/50',
              )}
            >
              {value === 'new' && !!n && <span className="h-2 w-2 rounded-full bg-red-500" aria-hidden />}
              {label}
              {n !== undefined && <span className="text-xs tabular-nums opacity-70">{n}</span>}
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo nội dung, tổ chức, email, trang…" className="pl-8" />
        </div>
        <Select value={type || 'all'} onValueChange={(v) => { setType(v === 'all' ? '' : v); setPage(1) }}>
          <SelectTrigger className="w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Mọi loại</SelectItem>
            {(['bug', 'idea', 'feature'] as FeedbackKind[]).map((k) => <SelectItem key={k} value={k}>{TYPE_LABEL[k]}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* Xuất đúng bộ lọc đang xem (tab trạng thái + loại + từ khoá) kèm link ảnh ký sẵn 7 ngày — mang file về máy xử lý. */}
        <Button
          variant="outline"
          className="sm:ml-auto"
          disabled={exporting}
          title="Tải tệp JSON các góp ý theo bộ lọc đang chọn, kèm link ảnh (mở được 7 ngày không cần đăng nhập)"
          onClick={async () => {
            setExporting(true)
            try {
              const n = await downloadFeedbackExport({ status, type, q: debouncedQ })
              toast.success(`Đã xuất ${n} góp ý — link ảnh trong tệp mở được trong 7 ngày`)
            } catch (e) {
              toast.error(platformApiError(e))
            } finally {
              setExporting(false)
            }
          }}
        >
          {exporting ? <Loader2 className="animate-spin" /> : <Download />} Xuất JSON
        </Button>
      </div>

      {isError ? (
        <ErrorState message="Không tải được danh sách góp ý" />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            loading={isLoading}
            emptyTitle={debouncedQ || status || type ? 'Không có góp ý phù hợp bộ lọc' : 'Chưa có góp ý nào'}
            rowKey={(f) => f.id}
            onRowClick={open}
          />
          {!!data && data.total > PAGE_SIZE && <Pagination page={page} limit={PAGE_SIZE} total={data.total} onPageChange={setPage} />}
        </>
      )}

      {selected && <FeedbackDetailDialog key={selected.id} item={selected} onClose={() => setSelected(null)} onSaved={setSelected} />}
    </div>
  )
}

/** Ảnh đính kèm: tải bằng token Platform → blob → thumbnail; bấm mở bản đầy đủ ở tab mới. */
function FeedbackThumb({ id, name }: { id: string; name: string }) {
  const { data: blob, isError } = useFeedbackImage(id, name)
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob])
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  if (isError) return <div className="flex h-24 w-24 items-center justify-center rounded-md border text-muted-foreground" title="Không tải được ảnh"><ImageOff className="h-5 w-5" /></div>
  if (!url) return <div className="h-24 w-24 animate-pulse rounded-md bg-muted" />
  return (
    <a href={url} target="_blank" rel="noreferrer" title="Mở ảnh đầy đủ">
      <img src={url} alt="Ảnh đính kèm" className="h-24 w-24 rounded-md border object-cover" />
    </a>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words text-sm">{children || '—'}</dd>
    </div>
  )
}

function FeedbackDetailDialog({ item, onClose, onSaved }: { item: FeedbackItem; onClose: () => void; onSaved: (f: FeedbackItem) => void }) {
  const [status, setStatus] = useState<FeedbackStatus>(item.status)
  const [note, setNote] = useState(item.adminNote)
  // Server có thể đổi trạng thái sau khi mở (mở góp ý "Mới" → tự chuyển "Đang xem") hoặc sau khi lưu:
  // đồng bộ lại ô chọn để không coi bản cũ là "chưa lưu" rồi ghi ngược về.
  useEffect(() => { setStatus(item.status) }, [item.status])
  useEffect(() => { setNote(item.adminNote) }, [item.adminNote])
  const update = useUpdateFeedback()
  const dirty = status !== item.status || note !== item.adminNote

  const save = () =>
    update.mutate(
      { id: item.id, status: status !== item.status ? status : undefined, adminNote: note !== item.adminNote ? note : undefined },
      {
        onSuccess: (u) => { toast.success('Đã lưu'); onSaved(u) },
        onError: (e) => toast.error(platformApiError(e)),
      },
    )

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
            {TYPE_LABEL[item.type] ?? item.type} <StatusBadge status={item.status} />
          </DialogTitle>
          <DialogDescription>
            {item.orgName || 'Không rõ tổ chức'} · {item.userName || item.userEmail} · {fmt(item.createdAt)}
          </DialogDescription>
        </DialogHeader>

        <p className="whitespace-pre-wrap rounded-lg bg-muted/50 p-3 text-sm">{item.message}</p>

        {item.images.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {item.images.map((n) => <FeedbackThumb key={n} id={item.id} name={n} />)}
          </div>
        )}

        <dl className="grid gap-3 sm:grid-cols-2">
          <Field label="Người gửi">{[item.userName, item.userEmail, item.role && `(${item.role})`].filter(Boolean).join(' ')}</Field>
          <Field label="Trang đang mở">{item.page}</Field>
          <Field label="Trình duyệt">{item.withDiagnostics ? item.userAgent : 'Người dùng không đính kèm thông tin kỹ thuật'}</Field>
          <Field label="Cỡ màn hình">{item.viewport}</Field>
          {item.impersonatedBy && <Field label="Gửi khi đang xem dưới quyền">Quản trị nền tảng {item.impersonatedBy}</Field>}
          {item.handledAt && <Field label="Xử lý lần cuối">{fmt(item.handledAt)}</Field>}
        </dl>

        {(item.errors.length > 0 || item.apiFailures.length > 0) && (
          <div className="space-y-2 text-xs">
            {item.errors.length > 0 && (
              <div>
                <p className="mb-1 font-medium">Lỗi JavaScript gần nhất ({item.errors.length})</p>
                <ul className="space-y-0.5 rounded-md border p-2 font-mono">
                  {item.errors.map((e, i) => <li key={i} className="break-words"><span className="text-muted-foreground">{timeOf(e.at)}</span> {e.message}{e.source && <span className="text-muted-foreground"> — {e.source}</span>}</li>)}
                </ul>
              </div>
            )}
            {item.apiFailures.length > 0 && (
              <div>
                <p className="mb-1 font-medium">API lỗi gần nhất ({item.apiFailures.length})</p>
                <ul className="space-y-0.5 rounded-md border p-2 font-mono">
                  {item.apiFailures.map((e, i) => <li key={i} className="break-words"><span className="text-muted-foreground">{timeOf(e.at)}</span> {e.method} {e.url} → <b>{e.status || 'mất mạng'}</b></li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="grid gap-3 border-t pt-3 sm:grid-cols-[200px_1fr]">
          <div className="space-y-1.5">
            <Label>Trạng thái</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as FeedbackStatus)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Ghi chú nội bộ (khách không thấy)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={2000} placeholder="Nguyên nhân, cách xử lý, link PR…" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Đóng</Button>
          <Button onClick={save} disabled={!dirty || update.isPending}>
            {update.isPending && <Loader2 className="animate-spin" />} Lưu
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
